import React, { useContext } from 'react'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import {
  Box,
  Button,
  Checkbox,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  Typography,
} from '@material-ui/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretDown, faCheck, faThumbtack } from '@fortawesome/pro-solid-svg-icons'
import { faEllipsisH, faImage } from '@fortawesome/pro-regular-svg-icons'
import { range } from 'lodash'
import cn from 'classnames'
import { Pill } from '@scoir/pills'
import ContentManagementContext, {
  ContentItem,
} from 'routes/content-management/ContentManagement/ContentManagementProvider/Context'

const useStyles = makeStyles((theme) => ({
  rowRoot: {
    verticalAlign: 'top',
  },
  image: {
    minWidth: 120,
    height: 120,
    borderRadius: 8,
  },
  emptyImage: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.disabled,
  },
  emptyImageIcon: {
    height: theme.spacing(3),
    width: theme.spacing(3),
  },
  contentTitleCell: {
    overflow: 'hidden',
    maxWidth: '250px',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical',
    '-webkit-line-clamp': 2,
  },
  menuIcon: {
    minWidth: theme.spacing(4),
  },
  // TODO: Update when pill component is updated
  topicPill: {
    width: 'max-content',
  },
  // TODO: Update to disabled with pills change
  seeMoreTagsIcon: {
    color: theme.palette.text.disabled,
  },
}))

type ContentTableRowProps = {
  data: ContentItem
  selected?: boolean
  viewOnly?: boolean
}

const ContentTableRow = ({ data, selected = false, viewOnly }: ContentTableRowProps) => {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)

  const { fetchContentItemDetails, onSelectContent, onUpdateContentVisibility } = useContext(ContentManagementContext)

  const handleRowClick = () => {
    fetchContentItemDetails(data.id, data.contentType)
  }

  const getBulkActionCell = () => {
    const handleSelectContent = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      const newSelectedContent = { id: data.id, contentType: data.contentType }
      const { target } = event as unknown as { target: HTMLInputElement }
      onSelectContent(target.checked, newSelectedContent)
    }
    if (viewOnly) return null
    return (
      <TableCell padding="checkbox">
        <Checkbox
          inputProps={{ 'aria-label': `Select ${data.title}` }}
          color="primary"
          checked={selected}
          onClick={handleSelectContent}
        />
      </TableCell>
    )
  }

  const getTitleCell = () => {
    const stripDescription = () => {
      const desc = new DOMParser().parseFromString(data.description, 'text/html')
      return desc.body.textContent || ''
    }
    return (
      <Box
        display="flex"
        gridGap={16}
      >
        {data.image.length ? (
          <img
            className={classes.image}
            src={data.image}
            alt={data.title}
          />
        ) : (
          <Box className={cn(classes.image, classes.emptyImage)}>
            <FontAwesomeIcon
              icon={faImage}
              className={classes.emptyImageIcon}
            />
          </Box>
        )}
        <Box>
          <Typography className={classes.contentTitleCell}>{data.title}</Typography>
          <Typography
            color="textSecondary"
            className={classes.contentTitleCell}
          >
            {stripDescription()}
          </Typography>
          {data.isFeatured && (
            <FeaturedPill>
              <FontAwesomeIcon icon={faThumbtack} />
              Featured
            </FeaturedPill>
          )}
        </Box>
      </Box>
    )
  }
  const getStatusCell = () => {
    const handleStatusClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      setAnchorEl(event.currentTarget)
    }
    const handleStatusClose = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      setAnchorEl(null)
    }
    const handleStatusChange = (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
      const newSelectedContent = { id: data.id, contentType: data.contentType }
      onUpdateContentVisibility(newSelectedContent, !data.isHidden)
      handleStatusClose(event as unknown as React.MouseEvent<HTMLButtonElement>)
    }
    return (
      <Box>
        {data.isHidden ? (
          <Button
            id="status-button"
            variant="outlined"
            color="primary"
            size="small"
            endIcon={<FontAwesomeIcon icon={faCaretDown} />}
            disabled={viewOnly}
            onClick={handleStatusClick}
            aria-controls="status-menu"
            aria-haspopup="true"
          >
            Hidden
          </Button>
        ) : (
          <SuccessButton
            id="status-button"
            variant="outlined"
            size="small"
            endIcon={<FontAwesomeIcon icon={faCaretDown} />}
            disabled={viewOnly}
            onClick={handleStatusClick}
            aria-controls="status-menu"
            aria-haspopup="true"
          >
            Live
          </SuccessButton>
        )}
        <Menu
          id="status-menu"
          getContentAnchorEl={null}
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleStatusClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <MenuItem
            onClick={handleStatusChange}
            selected={!data.isHidden}
          >
            <ListItemIcon className={classes.menuIcon}>
              {!data.isHidden && <FontAwesomeIcon icon={faCheck} />}
            </ListItemIcon>
            <ListItemText>Live</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={handleStatusChange}
            selected={data.isHidden}
          >
            <ListItemIcon className={classes.menuIcon}>
              {data.isHidden && <FontAwesomeIcon icon={faCheck} />}
            </ListItemIcon>
            <ListItemText>Hidden</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    )
  }

  const getContentType = () => {
    switch (data.contentType) {
      case 'CollegeOverview':
        return 'College Overview (Post)'
      case 'Academics':
        return 'Academics (Post)'
      case 'Admissions':
        return 'Admissions (Post)'
      case 'CostAndFinancialAid':
        return 'Cost & Aid (Post)'
      case 'StudentLife':
        return 'Student Life (Post)'
      case 'Sessions':
        return 'Event'
      case 'Programs':
        return 'Program'
      default:
        return 'Unknown'
    }
  }

  const getTopicsCell = () => {
    const allTopics = [...(data.academicFocus || []), ...(data.tags || [])]
    return (
      <Box
        display="flex"
        gridGap={8}
        flexWrap="wrap"
      >
        {range(0, 3).map((idx) => (
          <Pill
            key={idx}
            color="info"
            className={classes.topicPill}
          >
            {allTopics[idx]}
          </Pill>
        ))}
        {allTopics.length > 3 && (
          <Pill>
            <FontAwesomeIcon
              className={classes.seeMoreTagsIcon}
              icon={faEllipsisH}
            />
          </Pill>
        )}
      </Box>
    )
  }

  const getLastUpdatedCell = () => (
    <Box>
      <div>
        {new Intl.DateTimeFormat('en-US', {
          dateStyle: 'medium',
        }).format(new Date(data.lastUpdated))}
      </div>
      <div>
        {new Intl.DateTimeFormat('en-US', {
          timeStyle: 'long',
        }).format(new Date(data.lastUpdated))}
      </div>
    </Box>
  )

  return (
    <TableRow
      key={data.title}
      className={classes.rowRoot}
      hover
      onClick={handleRowClick}
    >
      <>{getBulkActionCell()}</>
      <TableCell>{getTitleCell()}</TableCell>
      <TableCell>{getStatusCell()}</TableCell>
      <TableCell>{getContentType()}</TableCell>
      <TableCell>{getTopicsCell()}</TableCell>
      <TableCell>{getLastUpdatedCell()}</TableCell>
    </TableRow>
  )
}

export default ContentTableRow
// TODO : REMOVE WHEN WE ADD SUCCESS BUTTON TO THEME
const SuccessButton = withStyles((theme) => ({
  root: {
    color: theme.palette.success.dark,
    borderColor: `rgba(88, 127, 27, 0.50)`,
    '&:hover': {
      borderColor: theme.palette.success.dark,
      backgroundColor: 'rgba(88, 127, 27, 0.08)',
    },
  },
}))(Button)

// TODO: REMOVE THIS AFTER UPDATING PILL COMPONENT
export const FeaturedPill = withStyles(() => ({
  root: {
    display: 'flex',
    gap: 4,
    backgroundColor: '#F4BE4A',
    width: 'fit-content',
  },
}))(Pill)
